import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { debounce } from 'lodash/fp';
import { compose } from 'redux';

import {
    Button,
    FlexColumn,
    FlexRow,
    H2,
    InputWithDebounce,
    P,
    PageBody,
    PageBodyFooter,
    PageContainer,
    PageHeader,
} from '@portal/common/components';
import {
    WithGoogleAnalyticsInjectedProps,
    withGoogleAnalytics,
} from '@portal/common/services/google-analytics';
import { styled } from '@portal/common/theme';
import { PaginationQueryParams } from '@portal/common/types';
import withQueryParams, {
    WithQueryParamsInjectedProps,
} from '@portal/common/utility/hoc/with-query-params';
import { WithRouterProps, withRouter } from '@portal/common/utility/hoc/with-router';

import api from '../../api';
import FavoriteResourceList from '../../components/FavoriteResourceList';
import _ from '../../locale';
import { GROUP_CONTENT_ID, MY_FAVORITES_ID, MY_PERMALINK } from '../../router/paths';
import { getOrganizationId } from '../../store/organization/selectors';
import { subscribeToListAsync, unsubscribeFromListAsync } from '../../store/resource-lists/actions';
import {
    getCustomLists,
    getPublicLists as getPublicListsSelector,
} from '../../store/resource-lists/selectors';
import { getSessionAccountId } from '../../store/session/selectors';
import ListSettings from './ListSettings';

const StyledPageHeader = styled(PageHeader)({
    minHeight: 100,
    height: 'auto',
    justifyContent: 'space-between',
    boxSizing: 'border-box',
});

const Header = styled('div')({
    width: '70%',
});

const inputStyle = { minWidth: 180 };

const ListTitle = styled(H2)(({ theme }) => ({
    ...theme.wrapLongText,
    paddingTop: 10,
}));

const ListDescription = styled(P)(({ theme }) => ({
    ...theme.wrapLongText,
}));

const DEFAULT_QUERY_PARAMS: PaginationQueryParams = {
    page: '1',
    per_page: '10',
    order_by: 'created',
    order_direction: 'DESC',
};

const HANDLE_RESIZE_TIMEOUT = 150;

type Props = WithQueryParamsInjectedProps<PaginationQueryParams> &
    WithGoogleAnalyticsInjectedProps &
    WithRouterProps<{ listId: string }>;

const MyListScene: FC<Props> = ({ match, queryParams, setQueryParams }) => {
    const dispatch = useDispatch();
    const organizationId = useSelector(getOrganizationId);
    const memberId = useSelector(getSessionAccountId);
    const memberLists = useSelector(getCustomLists);
    const publicLists = useSelector(getPublicListsSelector);

    const subscribeToList = (payload) => dispatch(subscribeToListAsync.request(payload));
    const unsubscribeFromList = (payload) => dispatch(unsubscribeFromListAsync.request(payload));

    const listId = useMemo(() => parseInt(match.params.listId), [match.params.listId]);
    const list = useMemo(() => {
        return (memberLists || []).concat(publicLists || []).find((i) => i.id === listId);
    }, [memberLists, publicLists, listId]);
    const isCreatedByMe = useMemo(
        () => list && list.creator && list.creator.id === memberId,
        [list, memberId]
    );
    const isInMyLists = useMemo(
        () => !!(memberLists || []).find((list) => list.id === listId),
        [memberLists, listId]
    );
    const isSubscribeButtonShown = useMemo(() => list && !isCreatedByMe, [list, isCreatedByMe]);

    const title = useMemo(() => {
        switch (match.params.listId) {
            case MY_FAVORITES_ID:
                return _.get('default_list_name');
            case GROUP_CONTENT_ID:
                return _.get('default_group_list_name');
            case MY_PERMALINK:
                return _.get('share_permalink_nav_link_label');
            default:
                return list?.name;
        }
    }, [match.params.listId, list]);

    const description = useMemo(() => {
        if ([MY_FAVORITES_ID, GROUP_CONTENT_ID, MY_PERMALINK].includes(match.params.listId)) {
            return '';
        } else {
            return list?.description;
        }
    }, [match.params.listId, list]);

    const [headerHeight, setHeaderHeight] = useState<number>(0);

    const headerRef = useRef<HTMLDivElement>(null);

    const updateHeaderHeight = useCallback(() => {
        const height = headerRef.current?.clientHeight;
        if (height !== headerHeight) {
            setHeaderHeight(height);
        }
    }, [headerHeight]);

    const getParams = useCallback(
        (params?: any) => ({
            organizationId,
            memberId,
            ...DEFAULT_QUERY_PARAMS,
            ...queryParams,
            ...params,
        }),
        [organizationId, memberId, queryParams]
    );

    const loadItems = useCallback(() => {
        switch (match.params.listId) {
            case MY_FAVORITES_ID:
                return api.organizationMemberFavoriteResource.getFavoriteResources(getParams());
            case GROUP_CONTENT_ID:
                return api.organizationGroupContent.getLinksAndFiles(getParams());
            case MY_PERMALINK:
                return api.permalink.getPermalinks(getParams());
            default:
                return api.organizationResourceListResource.getResourceListResources(
                    getParams({ listId })
                );
        }
    }, [match.params.listId, getParams, listId]);

    const toggleSubscription = useCallback(() => {
        if (isInMyLists) {
            unsubscribeFromList({ organizationId, memberId, listId });
        } else {
            subscribeToList({ organizationId, memberId, listId, prevList: list });
        }
    }, [organizationId, memberId, subscribeToList, unsubscribeFromList, listId, list, isInMyLists]);

    const handleDebouncedInput = useCallback(
        (value: string) =>
            setQueryParams({
                ...queryParams,
                search: value,
                page: '1',
            }),
        [queryParams]
    );

    const debouncedResizeHandler = useMemo(
        () => debounce(HANDLE_RESIZE_TIMEOUT, updateHeaderHeight),
        [updateHeaderHeight]
    );

    useEffect(() => {
        window.addEventListener('resize', debouncedResizeHandler);

        return () => {
            debouncedResizeHandler.cancel();
            window.removeEventListener('resize', debouncedResizeHandler);
        };
    }, [debouncedResizeHandler]);

    useEffect(() => {
        updateHeaderHeight();
    }, [updateHeaderHeight, list?.name, list?.description]);

    if (listId === null || memberLists === null) {
        return null;
    }

    return (
        <PageContainer>
            <StyledPageHeader ref={headerRef}>
                <Header>
                    <ListTitle>{title}</ListTitle>
                    <ListDescription>{description}</ListDescription>
                </Header>
                <FlexRow align="center" itemsSpacing={25}>
                    <InputWithDebounce
                        initialValue={queryParams.search}
                        debounceBy={250}
                        onDebounce={handleDebouncedInput}
                        style={inputStyle}
                        beforeIconSrc="/icons/ic-search.svg"
                        placeholder={_.get('search_placeholder')}
                    />
                    {isSubscribeButtonShown && (
                        <Button onClick={toggleSubscription}>
                            {_(isInMyLists ? 'lists_unsubscribe_button' : 'lists_subscribe_button')}
                        </Button>
                    )}
                    <ListSettings {...{ listId, list, isCreatedByMe }} />
                </FlexRow>
            </StyledPageHeader>

            <PageBody headerHeight={headerHeight}>
                <FlexColumn grow={1}>
                    <FavoriteResourceList
                        key={match.params.listId}
                        defaultQueryParams={DEFAULT_QUERY_PARAMS}
                        queryParams={queryParams}
                        setQueryParams={setQueryParams}
                        loadItems={loadItems}
                    />
                </FlexColumn>
                <PageBodyFooter>{_.get('footer_note')}</PageBodyFooter>
            </PageBody>
        </PageContainer>
    );
};

export default compose(
    withRouter,
    withQueryParams,
    withGoogleAnalytics({
        trackMethods: (props) => ({
            handleSearchChange: (search?: string) => {
                if (search && search.length > 1) {
                    props.trackEvent({
                        category: 'Content',
                        action: 'Search',
                        label: search,
                    });
                }
            },
        }),
    })
)(MyListScene);
